import {useQuery, useQueryClient} from "@tanstack/react-query";
import {useEffect, useState} from "react";
import {getAccount} from "../services/AccountService";
import {canUserAccessAdminPortal, setCurrentUser} from "../util/AuthUtil";
import {useLogout} from "./useLogout";

// Shared cache key for the account query. Exported so the sign-in flow can seed the cache with the
// payload it already fetched via authenticate(), keeping the per-page-load call count at exactly one.
export const ACCOUNT_QUERY_KEY = ["account"];

// Fetches the fresh /api/v2/account payload once per page load (staleTime: Infinity + the
// in-memory query cache means client-side navigation triggers no further calls), stores it as the
// current user, and logs the caller out when the fresh payload says they no longer have
// admin-portal access. This is the single account fetch and the single admin-portal-access gate
// for the whole app.
export function useAccount(enabled = true) {
    const queryClient = useQueryClient();
    // Lazy init: if the cache already holds an account entry (seeded by sign-in, or fetched
    // earlier in this same page load), it was already stored as the current user, so readiness is
    // immediate and the guard doesn't paint an empty frame on every client-side route change.
    const [isReady, setIsReady] = useState(() => queryClient.getQueryData(ACCOUNT_QUERY_KEY) !== undefined);

    const logout = useLogout();
    const {data, isError, error} = useQuery({
        queryKey: ACCOUNT_QUERY_KEY,
        queryFn: () => getAccount(),
        enabled: enabled,
        staleTime: Infinity,
        retry: false,
    });

    useEffect(() => {
        if (isError) {
            logout();
        }
    }, [isError, error]);

    useEffect(() => {
        if (data) {
            const message = canUserAccessAdminPortal(data);
            if (message !== null) {
                logout();
            } else {
                setCurrentUser(data);
                setIsReady(true);
            }
        }
    }, [data]);

    return [isReady];
}
